import { Controller } from "@hotwired/stimulus";

// Filters the affiliate "Provisionen" product list two ways at once (AND-combined):
//   1. Scope dropdown — "all" shows every row; "customized" shows only rows the user has
//      given their own commission (data-customized="true", owned by the affiliate-products
//      controller).
//   2. Search box — substring match on each row's name (data-affiliate-products-filter-term).
// It keeps both option labels' counts live and disables "customized" at 0.
// State is purely visual and resets to "all" on any re-render.
export default class extends Controller {
  static targets = [
    "input", "item", "empty",
    "mode", "allOption", "customizedOption",
  ];
  static values = { threshold: Number, mode: { type: String, default: "all" } };

  connect() {
    if (this.hasInputTarget) {
      this.inputTarget.hidden = this.itemTargets.length <= this.thresholdValue;
    }
    this.recount();
    this.applyFilters();
  }

  selectMode(event) {
    this.modeValue = event.target.value;
    this.applyFilters();
  }

  filter() {
    this.applyFilters();
  }

  // affiliate-products:rowschanged -> customization changed. Refresh counts, avoid a dead
  // "customized mode but nothing customized" state, then re-filter.
  refresh() {
    this.recount();
    if (this.modeValue === "customized" && this.customizedCount === 0) {
      this.modeValue = "all";
    }
    this.applyFilters();
  }

  // Reflect the active mode on the dropdown (also covers the forced reset).
  modeValueChanged() {
    if (this.hasModeTarget) this.modeTarget.value = this.modeValue;
    this.fitMode();
  }

  // A native select is as wide as its longest option, and iOS Safari ignores
  // text-align on selects — the shorter selected label then floats far from
  // the chevron. Size the box to the selected option instead. The probe
  // measures on document.body with the select's font: on mobile this runs
  // while the sheet <dialog> is still closed (display: none), where an
  // in-place probe would measure zero.
  fitMode() {
    if (!this.hasModeTarget) return;
    const select = this.modeTarget;
    const label = select.selectedOptions[0]?.textContent;
    if (!label) return;

    const styles = getComputedStyle(select);
    const probe = document.createElement("span");
    probe.style.cssText = `position: absolute; visibility: hidden; white-space: pre; font: ${styles.font}; letter-spacing: ${styles.letterSpacing};`;
    if (!probe.style.font) {
      probe.style.fontSize = styles.fontSize;
      probe.style.fontFamily = styles.fontFamily;
      probe.style.fontWeight = styles.fontWeight;
    }
    probe.textContent = label;
    document.body.appendChild(probe);
    select.style.width = `${
      probe.offsetWidth +
      parseFloat(styles.paddingLeft) +
      parseFloat(styles.paddingRight)
    }px`;
    probe.remove();
  }

  applyFilters() {
    const query = this.hasInputTarget ? this.inputTarget.value.trim().toLowerCase() : "";
    let visible = 0;

    this.itemTargets.forEach((item) => {
      const matchesMode = this.modeValue === "all" || item.dataset.customized === "true";
      const term = (item.dataset.affiliateProductsFilterTerm || "").toLowerCase();
      const matchesQuery = query === "" || term.includes(query);
      const show = matchesMode && matchesQuery;
      item.hidden = !show;
      if (show) visible += 1;
    });

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = !(this.itemTargets.length > 0 && visible === 0);
    }
  }

  recount() {
    const total = this.itemTargets.length;
    const customized = this.customizedCount;

    if (this.hasAllOptionTarget) this.relabel(this.allOptionTarget, total);
    if (this.hasCustomizedOptionTarget) {
      this.relabel(this.customizedOptionTarget, customized);
      this.customizedOptionTarget.disabled = customized === 0;
    }
    this.fitMode();
  }

  // The option labels are server-rendered i18n strings with a COUNT placeholder.
  relabel(option, count) {
    option.textContent = option.dataset.label.replace("COUNT", count);
  }

  get customizedCount() {
    return this.itemTargets.filter((item) => item.dataset.customized === "true").length;
  }
}
